import { useCallback, useEffect, useMemo, useState } from "react";
import type { Refs, Row } from "../types";
import { api, ApiError, type ContagemAgenda } from "../api";
import ErrorState from "./ErrorState";
import { usePodeExcluir } from "../permissoes";
import { IconClose, IconTrash, IconAlert } from "../icons";
import { ordenarPorNome } from "../ordem";

interface Props {
  refs: Refs;
}

/** Paleta de cores das células (mesmas do cronograma da SERCAMP). */
export const AGENDA_CORES: { key: string; label: string }[] = [
  { key: "preagendado", label: "Pré-agendado (aguardando cliente)" },
  { key: "confirmado", label: "Confirmado com cliente" },
  { key: "pessego", label: "Treinamento" },
  { key: "lavanda", label: "Externo (azul)" },
  { key: "rosa", label: "Externo (rosa)" },
  { key: "rosa-forte", label: "Integração" },
  { key: "verde", label: "Externo (verde)" },
  { key: "marrom", label: "Externo (marrom)" },
  { key: "laranja", label: "Externo (laranja)" },
  { key: "ciano", label: "Férias" },
  { key: "azul", label: "Fechado" },
  { key: "amarelo", label: "Folga" },
  { key: "branco", label: "Interno / Sercamp" },
];

const ROTULO_COR = new Map(AGENDA_CORES.map((c) => [c.key, c.label]));

/* ----------------------------- Helpers de data ----------------------------- */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
/** Segunda-feira da semana da data informada. */
function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // segunda = 0
  x.setDate(x.getDate() - dow);
  return x;
}

const fmtWeekday = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });
const fmtDay = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtRange = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });

/**
 * Agenda da Gestão da Operação.
 *
 * O cronograma continua sendo MONTADO no Planejamento (mesma base
 * `sercamp_planejamento`) — aqui não se cria nem se edita alocação. O que o
 * gestor pode fazer é DESMARCAR: apagar uma célula ou limpar um período
 * inteiro, que é o caso real de "essa semana foi remarcada, começa de novo".
 *
 * Apagar é exclusivo de administrador (mesma regra do resto do portal): quem
 * não é admin continua vendo a agenda em modo consulta, sem os botões.
 *
 * ⚠️ A tabela é COMPARTILHADA: o que sai daqui sai também do Planejamento e do
 * app do colaborador, inclusive folgas já aprovadas. Por isso as telas de
 * confirmação dizem, com número, o que vai junto.
 */
export default function AgendaView({ refs }: Props) {
  // Só administrador apaga (regra do Portal). Sem isso, a agenda fica como era:
  // uma grade de consulta.
  const podeExcluir = usePodeExcluir();
  const [start, setStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const [span, setSpan] = useState<number>(7);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  /** Célula aberta para exclusão (só abre quando há marcação). */
  const [cell, setCell] = useState<{ colab: Row; date: string; entry: Row } | null>(null);
  const [limpando, setLimpando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list("agenda"));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: span }, (_, i) => addDays(start, i)),
    [start, span],
  );

  const map = useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of rows) {
      const d = String(r.data).slice(0, 10);
      m.set(`${r.colaborador_id}|${d}`, r);
    }
    return m;
  }, [rows]);

  const today = ymd(new Date());
  // Linhas da grade em ordem alfabética CRESCENTE (A → Z). O servidor já manda
  // ordenado; a ordenação aqui é a garantia da TELA — é nela que a ordem foi
  // pedida, e ela não pode depender de quem chamou a API antes. A regra (pt-BR,
  // acento e maiúscula tratados — "Álvaro" perto de "Adolfo", não no fim) mora
  // em src/ordem.ts: é a mesma da Agenda do Planejamento e de todas as listas
  // de nomes deste sistema.
  const colaboradores = useMemo(
    () => ordenarPorNome(refs.colaboradores, (c) => c.nome),
    [refs.colaboradores],
  );

  /** Apaga UMA marcação (a célula aberta). */
  async function excluirCelula() {
    if (!cell) return;
    await api.remove("agenda", cell.entry.id);
    setCell(null);
    setAviso("Marcação apagada.");
    await load();
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const periodo = `${fmtRange.format(days[0])} a ${fmtRange.format(days[days.length - 1])}`;

  return (
    <div className="agenda agenda-readonly">
      <div className="agenda-banner">CRONOGRAMA DE SERVIÇOS INTERNOS E EXTERNOS</div>

      <div className="agenda-toolbar">
        <div className="agenda-nav">
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, -span))}>
            ‹ Anterior
          </button>
          <button className="btn-secondary" onClick={() => setStart(startOfWeekMonday(new Date()))}>
            Hoje
          </button>
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, span))}>
            Próximo ›
          </button>
          <span className="agenda-periodo">{periodo}</span>
        </div>
        <div className="agenda-span">
          <label>
            Período:&nbsp;
            <select value={span} onChange={(e) => setSpan(Number(e.target.value))}>
              <option value={7}>1 semana</option>
              <option value={11}>11 dias</option>
              <option value={14}>2 semanas</option>
            </select>
          </label>
          {podeExcluir && (
            <button
              className="btn-secondary btn-danger-text"
              style={{ marginLeft: 12 }}
              onClick={() => setLimpando(true)}
              title="Apagar as marcações de um período inteiro"
            >
              <IconTrash size={16} /> Limpar agenda
            </button>
          )}
        </div>
      </div>

      {podeExcluir && (
        <div className="crud-aviso">
          Para apagar uma marcação, <strong>clique na célula</strong>. O cronograma é montado no{" "}
          <strong>Planejamento</strong> — aqui só se desmarca. O que sai daqui sai também de lá e do
          aplicativo do colaborador.
        </div>
      )}

      {aviso && <div className="crud-aviso">{aviso}</div>}

      <div className="agenda-legend">
        {AGENDA_CORES.map((c) => (
          <span key={c.key} className="agenda-legend-item">
            <span className={`agenda-swatch ag-${c.key}`} />
            {c.label}
          </span>
        ))}
      </div>

      <div className="agenda-grid-wrap">
        {loading ? (
          <div className="empty">Carregando agenda...</div>
        ) : colaboradores.length === 0 ? (
          <div className="empty">Cadastre colaboradores na aba Equipe para montar a agenda.</div>
        ) : (
          <table className="agenda-grid">
            <thead>
              <tr>
                <th className="ag-corner">COLABORADORES</th>
                {days.map((d) => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th
                      key={ymd(d)}
                      className={`ag-head${weekend ? " ag-weekend" : ""}${ymd(d) === today ? " ag-today" : ""}`}
                    >
                      <span className="ag-head-dow">{fmtWeekday.format(d)}</span>
                      <span className="ag-head-date">{fmtDay.format(d)}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {colaboradores.map((colab) => (
                <tr key={colab.id}>
                  <th className="ag-name">{colab.nome}</th>
                  {days.map((d) => {
                    const key = `${colab.id}|${ymd(d)}`;
                    const entry = map.get(key) ?? null;
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    // Célula vazia não abre nada: aqui não se CRIA marcação
                    // (isso é do Planejamento), só se apaga o que existe.
                    const clicavel = podeExcluir && !!entry;
                    return (
                      <td
                        key={key}
                        className={`ag-cell${weekend ? " ag-weekend" : ""}`}
                        style={clicavel ? { cursor: "pointer" } : undefined}
                        title={clicavel ? "Clique para apagar esta marcação" : undefined}
                        onClick={
                          clicavel
                            ? () => setCell({ colab, date: ymd(d), entry: entry as Row })
                            : undefined
                        }
                      >
                        {entry ? (
                          <span className={`ag-block ag-${entry.cor}`}>{entry.servico}</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {cell && (
        <ExcluirCelulaModal
          colabNome={String(cell.colab.nome ?? "")}
          dateLabel={fmtDay.format(new Date(cell.date + "T00:00:00"))}
          entry={cell.entry}
          onCancel={() => setCell(null)}
          onDelete={excluirCelula}
        />
      )}

      {limpando && (
        <LimparAgendaModal
          de={ymd(days[0])}
          ate={ymd(days[days.length - 1])}
          periodoLabel={periodo}
          onCancel={() => setLimpando(false)}
          onDone={async (msg) => {
            setLimpando(false);
            setAviso(msg);
            await load();
          }}
        />
      )}
    </div>
  );
}

/* -------------------------- Apagar UMA marcação --------------------------- */
function ExcluirCelulaModal({
  colabNome,
  dateLabel,
  entry,
  onCancel,
  onDelete,
}: {
  colabNome: string;
  dateLabel: string;
  entry: Row;
  onCancel: () => void;
  onDelete: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ehFolga = entry.cor === "amarelo";
  const doPlanejamento = entry.atividade_id != null;

  async function confirmar() {
    setSaving(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao apagar");
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card agenda-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Apagar marcação</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="agenda-modal-info">
            <strong>{colabNome}</strong>
            <span className="muted">{dateLabel}</span>
          </div>

          <div className="agenda-detalhe">
            <span className={`ag-block ag-${entry.cor}`}>{entry.servico || "(sem descrição)"}</span>
            <p className="muted">{ROTULO_COR.get(String(entry.cor)) ?? String(entry.cor)}</p>
          </div>

          {/* O que a pessoa precisa saber ANTES de confirmar — não depois. */}
          {ehFolga && (
            <p className="agenda-alerta">
              <IconAlert size={16} /> Esta é uma <strong>folga</strong> do colaborador. Apagar remove
              o pedido dele, inclusive do aplicativo.
            </p>
          )}
          {doPlanejamento && (
            <p className="agenda-alerta">
              <IconAlert size={16} /> Este dia veio de uma <strong>OS do Planejamento</strong>.
              Apagar libera o colaborador, mas a atividade continua planejada lá.
            </p>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary btn-danger"
              onClick={confirmar}
              disabled={saving}
            >
              <IconTrash size={16} /> {saving ? "Apagando..." : "Apagar marcação"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Limpar um período ---------------------------- */
function LimparAgendaModal({
  de,
  ate,
  periodoLabel,
  onCancel,
  onDone,
}: {
  de: string;
  ate: string;
  periodoLabel: string;
  onCancel: () => void;
  onDone: (mensagem: string) => Promise<void>;
}) {
  /** "periodo" = o que está na tela; "tudo" = a agenda inteira. */
  const [alcance, setAlcance] = useState<"periodo" | "tudo">("periodo");
  const [contagem, setContagem] = useState<ContagemAgenda | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [cienteTudo, setCienteTudo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A contagem vem do servidor a cada troca de alcance: é o número que aparece
  // no botão, e ele não pode ser um palpite do que está carregado na tela.
  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setError(null);
    const p =
      alcance === "periodo" ? api.agenda.previaLimpeza(de, ate) : api.agenda.previaLimpeza();
    p.then((c) => {
      if (vivo) setContagem(c);
    })
      .catch((err) => {
        if (vivo) setError(err instanceof Error ? err.message : "Falha ao contar as marcações");
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [alcance, de, ate]);

  const total = contagem?.total ?? 0;
  // Apagar a agenda INTEIRA pede um passo a mais de propósito: é a única ação
  // aqui que não dá para desfazer olhando a tela seguinte.
  const travado = alcance === "tudo" && !cienteTudo;

  async function confirmar() {
    setSaving(true);
    setError(null);
    try {
      const r =
        alcance === "periodo"
          ? await api.agenda.limpar({ de, ate })
          : await api.agenda.limpar({ tudo: true });
      const detalhe =
        r.folgas || r.planejamento
          ? ` (${r.folgas} folga(s) e ${r.planejamento} dia(s) de OS do Planejamento)`
          : "";
      await onDone(
        r.apagadas === 0
          ? "Não havia marcação para apagar nesse período."
          : `${r.apagadas} marcação(ões) apagada(s)${detalhe}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao limpar a agenda");
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card agenda-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Limpar agenda</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <label className="agenda-opcao">
            <input
              type="radio"
              name="alcance"
              checked={alcance === "periodo"}
              onChange={() => {
                setAlcance("periodo");
                setCienteTudo(false);
              }}
            />
            <span>
              <strong>Somente o período na tela</strong>
              <span className="muted"> — {periodoLabel}</span>
            </span>
          </label>

          <label className="agenda-opcao">
            <input
              type="radio"
              name="alcance"
              checked={alcance === "tudo"}
              onChange={() => setAlcance("tudo")}
            />
            <span>
              <strong>Toda a agenda</strong>
              <span className="muted"> — todas as datas, passadas e futuras</span>
            </span>
          </label>

          <div className="agenda-contagem">
            {carregando ? (
              <span className="muted">Contando as marcações...</span>
            ) : (
              <>
                <strong>{total}</strong> marcação(ões) serão apagadas
                {contagem && (contagem.folgas > 0 || contagem.planejamento > 0) && (
                  <p className="agenda-alerta">
                    <IconAlert size={16} />
                    <span>
                      Vão junto <strong>{contagem.folgas}</strong> folga(s) de colaborador e{" "}
                      <strong>{contagem.planejamento}</strong> dia(s) de OS vindos do Planejamento.
                      A agenda é a mesma nos três sistemas.
                    </span>
                  </p>
                )}
              </>
            )}
          </div>

          {alcance === "tudo" && (
            <label className="agenda-opcao agenda-opcao-perigo">
              <input
                type="checkbox"
                checked={cienteTudo}
                onChange={(e) => setCienteTudo(e.target.checked)}
              />
              <span>
                Entendo que isto apaga o <strong>cronograma inteiro</strong> — inclusive folgas e
                alocações do Planejamento — e que não há como desfazer.
              </span>
            </label>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary btn-danger"
              onClick={confirmar}
              disabled={saving || carregando || travado || total === 0}
            >
              <IconTrash size={16} />
              {saving ? "Apagando..." : total === 0 ? "Nada para apagar" : `Apagar ${total}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
